import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to waste hours of life with fetch() and a bit of brainfart",
  "description": "",
  "published": "2016-11-01T00:00:00.000Z",
  "redirect_from": ["/blog/waste-hours-fetch-brainfart/swizec/7173"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Today, I had a dumb idea: `}<em parentName="p">{`"Nested superagent calls sure look messy… maybe I should use fetch() instead…”`}</em></p>
    <p>{`What happened next was a little bit of this:`}</p>
    <p><img parentName="p" {...{
        "src": "/7a209e5150b0d3705206133faef2cd0a/media-Kh296M49Zr1Sg-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Followed by a lot of this:`}</p>
    <p><img parentName="p" {...{
        "src": "/767bf389ecbae3c96f85995afdb3f180/media-X83Y7r03T6uty-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`In case you don't know, `}<a parentName="p" {...{
        "href": "https://github.com/visionmedia/superagent"
      }}>{`superagent`}</a>{` is a popular library for executing AJAX requests in JavaScript. Clean API, works well, makes life easy. I've been a fan for years.`}</p>
    <p><a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API"
      }}>{`fetch()`}</a>{` is a new JavaScript API for the same. Clean API, Promise-based interface, solves a problem you thought was solved. Not yet an official standard, but supported in all modern browsers. You can use it in production code, if you're compiling with Babel and enable `}<inlineCode parentName="p">{`babel-polyfill`}</inlineCode>{`.`}</p>
    <p>{`Both superagent and fetch() enable you to talk to a server. The first produces clean code that gets nesty if you need many things. The second produces clean code that is Promis-y and sometimes cumbersome.`}</p>
    <p>{`For example, to get a JSON object with superagent, you'd do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`superagent.get('/api/some.json')
                    .set('Accept, 'application/json')
                    .end((err, res) => {
                        // res.body contains parsed JSON
                    });
`}</code></pre>
    <p>{`The same call with fetch() looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch("/api/some.json", { headers: { Accept: "application/json" } })
  .then((res) => res.json())
  .then((json) => {
    // json contains parsed JSON
  });
`}</code></pre>
    <p>{`Both just 5 lines of code. It’s debatable which is cleaner. They look the same to me. ¯`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p>{`Where it gets interesting is when you have two calls that rely on each other. Observe:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`superagent.get('/api/some.json')
                    .set('Accept, 'application/json')
                    .end((err, res) => {
                        const url = \`/api/details/\${res.body.details_id}.json\`;
                        superagent.get(url)
                                            .set('Accept', 'application/json')
                                            .end((err, res) => {
                                                // res.body contains parsed details
                                            });
                    });
`}</code></pre>
    <p>{`Vs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch("/api/some.json", { headers: { Accept: "application/json" } })
  .then((res) => res.json())
  .then((json) => {
    const url = \`/api/details/\${json.details_id}.json\`;
    return fetch(url, { headers: { Accept: "application/json" } });
  })
  .then((res) => res.json())
  .then((json) => {
    // json contains parsed details
  });
`}</code></pre>
    <p>{`The promises approach `}<em parentName="p">{`does`}</em>{` look cleaner ?`}</p>
    <h2 {...{
      "id": "but-theres-a-catch-with-fetch"
    }}>{`But there's a catch with fetch()`}</h2>
    <p>{`See the `}<inlineCode parentName="p">{`Accept`}</inlineCode>{` header? Superagent sends it as an `}<inlineCode parentName="p">{`Accept`}</inlineCode>{` header. Fetch sends it as `}<inlineCode parentName="p">{`accept`}</inlineCode>{`.`}</p>
    <p>{`Your clean code stops working. You're doing everything right: you send the `}<inlineCode parentName="p">{`Accept`}</inlineCode>{` header, you send the `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` header for your API's token-based authentication, and yet…`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`TIL: Rails doesn't think HTTP headers are case-insensitive.`}<br parentName="p"></br><br parentName="p"></br>{`fetch() knows they are and forces lowercase `}<a parentName="p" {...{
          "href": "https://t.co/3DyMz7mgc6"
        }}>{`pic.twitter.com/3DyMz7mgc6`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/793197010664824832"
      }}>{`October 31, 2016`}</a></blockquote>
    <p>{`You fall into a rabbit hole…`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Rails can still get the token out of Authentication header, but returns 401 anyway.`}<br parentName="p"></br><br parentName="p"></br>{`This is what I get for trying to uses Promises.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/793200932485894144"
      }}>{`October 31, 2016`}</a></blockquote>
    <p>{`You read through all of the relevant questions and answers on StackOverflow. You google and google. You spelunk through Rails's code on Github.`}</p>
    <p>{`You abandon all hope…`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Oh great, looks like it's actually Warden bug deep inside the bowels of Devise.`}<br parentName="p"></br><br parentName="p"></br>{`All I wanted was to use fetch() 😭`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/793210701175148544"
      }}>{`October 31, 2016`}</a></blockquote>
    <p>{`You're about ready to start throwing things. Nothing makes sense, this is dumb, everything sucks, all you wanted was to Do The Right Thing™ and now you're stuck debugging huge frameworks.`}</p>
    <p>{`?`}</p>
    <p>{`By the way, Devise is a library for user authentication, and Warden is the core authentication library it wraps. No, I don't know why this happens in two libraries. Maybe historical reasons.`}</p>
    <p>{`And then it hits you. You're being an idiot.`}</p>
    <p>{`The difference between superagent and fetch() isn't that one sends your headers as-given and the other lowercases their names. The difference is that superagent sends a cookie and fetch() doesn't!`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Oh god ... the REAL issue is that fetch() doesn't automatically send Cookies and I didn't notice.`}<br parentName="p"></br><br parentName="p"></br>{`Fuck my life. `}<a parentName="p" {...{
          "href": "https://t.co/hWbxA8bvzV"
        }}>{`pic.twitter.com/hWbxA8bvzV`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/793215715939463168"
      }}>{`October 31, 2016`}</a></blockquote>
    <p>{`Your API relies on tokens to authenticate the client, `}<em parentName="p">{`and on session cookies to identify users`}</em>{`. ? It makes total sense, super common design. But `}<strong parentName="p"><em parentName="strong">{`ugh!!`}</em></strong></p>
    <p>{`You have to add `}<inlineCode parentName="p">{`credentials: 'same-origin'`}</inlineCode>{` to your fetch() settings object. Then it works. The API talks to you, the JavaScript client does its thing, users are happy.`}</p>
    <p>{`Code looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch("/api/some.json", {
  headers: { Accept: "application/json" },
  credentials: "same-origin",
})
  .then((res) => res.json())
  .then((json) => {
    const url = \`/api/details/\${json.details_id}.json\`;
    return fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  })
  .then((res) => res.json())
  .then((json) => {
    // json contains parsed details
  });
`}</code></pre>
    <p>{`In real code, I suggest wrapping fetch() in a helper function that always adds API-specific options like `}<inlineCode parentName="p">{`Accept`}</inlineCode>{` and `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` headers and `}<inlineCode parentName="p">{`credentials`}</inlineCode>{`. Your future self will thank you.`}</p>
    <p>{`No, I don't know why fetch() breaks the 20-year old convention that cookies are automatically included in requests.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      